import React, { useState } from "react";
import { X, Check, LayoutTemplate } from "lucide-react";
import TemplateRenderer from "./templates/TemplateRenderer";
import { LAYOUTS, TEMPLATE_CATEGORIES, getAccentHex } from "./templates/templateConfig";

/** Thumbnail-izes an arbitrary-width live TemplateRenderer render by rendering it at a fixed
 * "design width" inside a scaled + clipped wrapper — avoids needing to generate/store 23 static
 * preview images while still showing the learner's OWN resume content in every template. */
function TemplatePreviewThumb({ templateId, colorId, draft }) {
  return (
    <div className="relative w-full h-[210px] overflow-hidden rounded-lg bg-slate-100" data-testid={`resume-template-thumb-${templateId}`}>
      <div className="absolute top-0 left-0 w-[700px] origin-top-left" style={{ transform: "scale(0.29)" }}>
        <TemplateRenderer templateId={templateId} colorId={colorId} draft={draft} />
      </div>
    </div>
  );
}

export default function TemplateGalleryModal({ draft, onClose, onSelect }) {
  const [category, setCategory] = useState("All");
  const filtered = category === "All" ? LAYOUTS : LAYOUTS.filter((t) => t.category === category);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="resume-template-gallery-modal">
      <div className="bg-[#0d1220] border border-white/10 rounded-2xl w-full max-w-5xl max-h-[88vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#0d1220] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2"><LayoutTemplate className="w-5 h-5 text-indigo-300" /> Choose a Resume Template</h2>
            <p className="text-xs text-slate-400 mt-0.5">{LAYOUTS.length} templates · your own content shown in every preview · you can change this anytime</p>
          </div>
          <button onClick={onClose} data-testid="resume-template-gallery-close-btn" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex flex-wrap gap-2 px-6 pt-4" data-testid="resume-template-category-tabs">
          {TEMPLATE_CATEGORIES.map((c) => (
            <button
              key={c} onClick={() => setCategory(c)} data-testid={`resume-template-category-${c.toLowerCase()}`}
              className={`text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${category === c ? "bg-indigo-500 text-white border-indigo-500" : "bg-white/5 text-slate-300 border-white/10 hover:border-indigo-400/40"}`}
            >
              {c} {c === "All" ? `(${LAYOUTS.length})` : `(${LAYOUTS.filter((t) => t.category === c).length})`}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
          {filtered.map((t) => {
            const selected = draft.template === t.id;
            return (
              <div key={t.id} className={`rounded-xl border p-3 transition-colors ${selected ? "border-indigo-400" : "border-white/10 hover:border-white/25"}`} data-testid={`resume-template-card-${t.id}`}>
                <TemplatePreviewThumb templateId={t.id} colorId={draft.template_color} draft={draft} />
                <div className="flex items-center justify-between mt-3">
                  <p className="text-sm font-semibold text-white">{t.label}</p>
                  {t.atsFriendly && <span className="text-[9px] font-bold uppercase bg-emerald-400/15 text-emerald-300 px-1.5 py-0.5 rounded-full">ATS</span>}
                </div>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{t.description}</p>
                <button
                  onClick={() => onSelect(t.id)} data-testid={`resume-choose-template-${t.id}`}
                  className={`w-full mt-3 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg transition-colors ${selected ? "bg-indigo-500 text-white" : "bg-white/5 hover:bg-white/10 text-slate-200"}`}
                  style={selected ? { backgroundColor: getAccentHex(draft.template_color) } : {}}
                >
                  {selected ? <><Check className="w-3.5 h-3.5" /> Selected</> : "Choose Template"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
